import { startTransition, useEffect, useState } from 'react';

import { discountTypes } from 'lib/constants/constants';
import { cn, inRn, mString } from 'lib/helpers/utility';
import { DEFAULT_TIP_PERCENTAGES } from 'lib/constants/transaction/tips';

import Modal from 'components/shared/Modal/Modal';
import Typography from 'components/shared/Typography/Typography';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';

// `CurrencyInput` keeps percentages in hundredths of a percent — 10000 is 100%.
const MAX_PERCENT = 10000;

const toCents = (value) => inRn(Math.round(Number(value)));
const isSameAmount = (a, b) => toCents(a) === toCents(b);
const percentToCents = (percentage, base) => toCents((inRn(percentage) * base) / MAX_PERCENT);
const centsToPercent = (amount, base) =>
  base > 0 ? Math.min(MAX_PERCENT, Math.round((inRn(amount) / base) * MAX_PERCENT)) : 0;

const OPTION_CLASS = cn(
  'flex w-full cursor-pointer items-center justify-center rounded-xl border border-solid bg-white px-3 transition-all duration-200',
  '[@media(hover:hover)]:hover:border-primary-300 [@media(hover:hover)]:hover:shadow-card-selected'
);
const SELECTED_OPTION_CLASS = 'border-primary-300 shadow-card-selected ring-2 ring-primary-200';

const GratuityOption = ({ isSelected, onClick, dataQa, className, children }) => (
  <button
    type="button"
    aria-pressed={isSelected}
    data-qa={dataQa}
    onClick={onClick}
    className={cn(
      OPTION_CLASS,
      isSelected ? SELECTED_OPTION_CLASS : 'border-neutral-200',
      className
    )}>
    {children}
  </button>
);

const GratuityModal = ({
  isOpen,
  onClose,
  baseAmount,
  currentGratuity = 0,
  onApply,
  percentages = DEFAULT_TIP_PERCENTAGES
}) => {
  const [isCustom, setIsCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState(currentGratuity);
  const [customPercentage, setCustomPercentage] = useState(0);
  const [customUnit, setCustomUnit] = useState('amount');

  const isPercentUnit = customUnit === 'percent';
  const customCents = isPercentUnit
    ? percentToCents(customPercentage, baseAmount)
    : toCents(customAmount);
  const canApplyCustom = customCents > 0 && !isSameAmount(customCents, currentGratuity);

  const presetAmount = (percentage) => toCents((baseAmount * percentage) / 100);
  const isPresetSelected = (percentage) =>
    !isCustom && currentGratuity > 0 && isSameAmount(presetAmount(percentage), currentGratuity);

  useEffect(() => {
    if (!isOpen) return;

    const matchesPreset = percentages.some((percentage) =>
      isSameAmount(toCents((baseAmount * percentage) / 100), currentGratuity)
    );

    setIsCustom(currentGratuity > 0 && !matchesPreset);
    setCustomUnit('amount');
    setCustomAmount(currentGratuity);
    setCustomPercentage(centsToPercent(currentGratuity, baseAmount));
  }, [isOpen, currentGratuity, baseAmount, percentages]);

  // The two units mirror each other so the entered value survives a unit switch.
  const handleUnitChange = (unit) => {
    if (unit === customUnit) return;

    if (unit === 'percent') setCustomPercentage(centsToPercent(customAmount, baseAmount));
    else setCustomAmount(percentToCents(customPercentage, baseAmount));

    setCustomUnit(unit);
  };

  // Applying re-renders the whole payment form; as a transition it no longer holds the
  // modal on screen until that render finishes, so the close animation starts at once.
  const handleApply = (amount) => {
    onClose();
    startTransition(() => onApply(amount));
  };

  const handleCustomKeyDown = (event) => {
    if (event.key !== 'Enter' || !canApplyCustom) return;
    event.preventDefault();
    handleApply(customCents);
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      title="Add a tip?"
      width="small"
      isCentered
      modalCenterAnimation
      className="rounded-lg"
      isFooter={isCustom}
      // The tip changes the amount charged; Enter in the tip field must not apply it.
      submitOnEnter={false}
      footerActions={{
        secondary: { text: 'Cancel', onClick: onClose },
        primary: {
          text: 'Add tip',
          isDisabled: !canApplyCustom,
          onClick: () => handleApply(customCents),
          'data-qa': 'apply-gratuity-btn'
        }
      }}
      bodyClassName="p-0">
      <div className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-3 gap-3">
          {percentages.map((percentage) => (
            <GratuityOption
              key={percentage}
              isSelected={isPresetSelected(percentage)}
              onClick={() => handleApply(presetAmount(percentage))}
              dataQa={`gratuity-${percentage}`}
              className="flex-col gap-1 py-5">
              <Typography variant="h5" weight="medium" color="neutral-900">
                {`${percentage}%`}
              </Typography>
              <Typography variant="p" size="small" weight="medium" color="primary-500">
                {mString(presetAmount(percentage))}
              </Typography>
            </GratuityOption>
          ))}
        </div>

        <GratuityOption onClick={() => handleApply(0)} dataQa="gratuity-none" className="h-12">
          <Typography variant="p" size="large" weight="medium" color="neutral-900">
            No tip
          </Typography>
        </GratuityOption>

        <GratuityOption
          isSelected={isCustom}
          onClick={() => setIsCustom((prev) => !prev)}
          dataQa="gratuity-custom-toggle"
          className="h-12">
          <Typography variant="p" size="large" weight="medium" color="neutral-900">
            Custom amount
          </Typography>
        </GratuityOption>

        {isCustom && (
          <div className="flex flex-col gap-2 pt-2" onKeyDown={handleCustomKeyDown}>
            <CurrencyInput
              wrapperClassName="overflow-hidden !py-0 !pr-0"
              units={discountTypes}
              currentUnit={discountTypes.find((type) => type.value === customUnit)}
              onUnitChange={handleUnitChange}
              value={isPercentUnit ? customPercentage : customAmount}
              maxValue={isPercentUnit ? MAX_PERCENT : undefined}
              onValueChange={isPercentUnit ? setCustomPercentage : setCustomAmount}
              autoFocus
              data-qa="gratuity-custom-input"
            />
            <Typography variant="p" size="small" color="neutral-700">
              Any amount is appreciated. Press enter to add.
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GratuityModal;
